import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';

import { ibeSidebarAppointmentRead } from 'actions/appointment/beSidebarAppointment.action';
import xeRebook from 'actions/booking/xeRebook.action';
import fetchEventsBySalon from 'actions/scheduler/fetchEventsBySalon.action';
import fetchProceduresBySalon from 'actions/scheduler/fetchProceduresBySalon.action';
import ICO_CLOSE from 'assets/images/btn-close.svg';
import { fromPathnameToIsWeek } from 'bl/pathname.bl';
import DatePickerInput from 'components/form/DatePickerInput.component';
import LINK from 'constants/link.const';
import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import { useAppointmentPermissions } from 'hooks/useAppointmentPermissions.hook';
import { useNavFor } from 'hooks/useNav.hook';
import { usePathname } from 'hooks/usePathname.hook';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useApmStatus } from 'providers/AppointmentMachineProvider.component';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectSchedulerDayView } from 'selectors/scheduler.selectors';
import { selectSidebarAppointmentId } from 'selectors/sidebar.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { formatYearToDayCompact } from 'utils/date/format.util';
import { nowAsDate, nowAsIso } from 'utils/date/now.util';
import { getSchedulerWeekEndSunday, getSchedulerWeekStartMonday } from 'utils/date/schedulerWeekBound.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import { isTimeSameDay } from 'utils/predicates/time/compare.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';
import randomString from 'utils/values/randomString.util';

import { setCurrentDay, setView } from 'scheduler/actions';

import CN from './RebookAppointmentDialog.module.scss';

const L = logger('RebookAppointmentDialog'); // eslint-disable-line no-unused-vars

export const REBOOK_TIME = Object.freeze({
  after: 'afterCheckout',
  before: 'beforeCheckout',
});

export const RebookAppointmentDialog = ({ tag, onClose }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nav = useNavFor('RebookAppointmentDialog');
  const pathname = usePathname();
  const routeMatch = useRouteMatch();

  const isWeek = fromPathnameToIsWeek(routeMatch?.path ?? '');
  const { isSlideoutPersonalOnly, accessStylistId } = useAppointmentPermissions();
  const stylistIdFromPath = fromAnyToInteger(pathname.split('/').pop()) || void 1;
  const stylistId = isSlideoutPersonalOnly && accessStylistId ? accessStylistId : stylistIdFromPath;

  const salonId = useCurrentSalonId();
  const apStatus = useApmStatus();
  const appointmentId = useSelector(selectSidebarAppointmentId);
  const isDayView = useSelector(selectSchedulerDayView);
  const appointmentResult = usePrepareReadOnly(ibeSidebarAppointmentRead, { appointmentId });
  const rawAppointment = appointmentResult?.data?.originalJson?.results;

  const [selectedDateFromState, setSelectedDate] = useState(nowAsIso());
  const { val: hoverPastDay, tip: toggleHoverPastDay } = useTip(false);

  const onPick = useCallback(
    (ev) => {
      const raw = ev?.raw;
      setSelectedDate(fromAnyDateToIso(raw));

      if (isTimeSameDay(nowAsDate(), raw)) return;

      // NOTE: used to have if (from ===  REBOOK_TIME.before) deepFreeze(oldAppointment);

      dispatch(xeRebook({ ...(rawAppointment ?? void 1) }, AP_STATUS.paid === apStatus ? REBOOK_TIME.after : REBOOK_TIME.before, raw, fromAnyDateToIso(rawAppointment?.procedures?.[0]?.start_time)));

      if (isWeek) {
        const m = moment(raw);
        const bgn = fromAnyDateToIso(getSchedulerWeekStartMonday(m));
        const end = fromAnyDateToIso(getSchedulerWeekEndSunday(m));

        L.unignore('onPick()', 'fetchProceduresBySalon:', dispatch(fetchProceduresBySalon(salonId, bgn, end, stylistId)));
        L.unignore('onPick()', 'fetchEventsBySalon:', dispatch(fetchEventsBySalon(salonId, bgn, end, stylistId)));
      } else {
        L.unignore('onPick()', 'fetchProceduresBySalon:', dispatch(fetchProceduresBySalon(salonId, raw)));
        L.unignore('onPick()', 'fetchEventsBySalon:', dispatch(fetchEventsBySalon(salonId, raw)));
      }

      call('RebookAppointmentDialog.onClose', onClose);

      nav.push({
        pathname: linkTo({
          pattern: isWeek ? LINK.bookingWeekNewServices : LINK.bookingDayNewServices,
          params: {
            salonId,
            stylistId: isWeek ? stylistId : void 1,
            dateString: formatYearToDayCompact(raw),
          },
        }),
      });
    },
    [dispatch, rawAppointment, apStatus, isWeek, onClose, nav, salonId, stylistId],
  );

  const handleDateChange = useCallback(
    (date) => {
      const bgn = date.startOf('day');

      dispatch(setCurrentDay(bgn));
      dispatch(setView(isDayView, bgn, isWeek ? stylistId : void 1));
      onPick({ raw: bgn });
    },
    [dispatch, isDayView, isWeek, stylistId, onPick],
  );

  const handleWeekChange = useCallback(
    (date) => {
      const bgn = date.startOf('day');

      dispatch(setCurrentDay(bgn));
      dispatch(setView(isDayView, bgn, isWeek ? stylistId : void 1));
      onPick({ raw: bgn });
    },
    [dispatch, isDayView, isWeek, stylistId, onPick],
  );

  const handleIconClick = useCallback(() => call('RebookAppointmentDialog.onClose', onClose), [onClose]);
  const randomId = randomString();

  L.debug('()', { tag, onClose, appointmentId, isDayView });

  return (
    <div
      data-bem="RebookAppointmentDialog"
      data-tag={fromAnyToTrimmed(tag)}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
        [CN.fromSlideOutFooter]: !tag,
        [CN.fromWizard]: tag,
        'rebook-dpicker-input from-appointment-card': true,
      })}
    >
      <div role="button" className="close-style" tabIndex={0} onClick={handleIconClick}>
        <img src={ICO_CLOSE} alt="" />
      </div>
      <div className="picker-overlay" style={{ zIndex: '-1' }} />
      <div
        className={cn({
          [CN.pickerName]: true,
          [CN.fromSlideOutFooter]: !tag,
          [CN.fromWizard]: tag,
        })}
      >
        {t('composite.rebookAppointmentDialog.selectDate')}
      </div>

      <div
        className="co-date-picker"
        id={randomId}
        onMouseOver={(e) => {
          if (e.target.closest('.react-datepicker__day.is-past-day')) toggleHoverPastDay();
        }}
        onMouseOut={(e) => {
          if (e.target.closest('.react-datepicker__day.is-past-day')) toggleHoverPastDay();
        }}
      >
        <DatePickerInput
          tag={ct('RebookAppointmentDialog__common-date-picker', tag)}
          component={DatePickerInput.CommonDatePicker}
          inline
          selected={moment(selectedDateFromState)}
          locale="en-GB"
          onOutsideClick={alwaysNoop}
          onChange={handleDateChange}
          dayClassName={(date) => moment(date).isBefore(moment(), 'day') && 'is-past-day'}
        >
          <div className={CN.pastDateTooltip} style={{ display: hoverPastDay ? 'block' : 'none' }}>
            {t('composite.rebookAppointmentDialog.selectPastDate')}
          </div>
        </DatePickerInput>
        <DatePickerInput tag={ct('RebookAppointmentDialog__week-component', tag)} component={DatePickerInput.WeeksComponent} onChangeDate={handleWeekChange} selectedDate={selectedDateFromState} />
      </div>
    </div>
  );
};
